/**
 * Formats a pound amount value
 * @param {string|number} val - The value to format
 * @param {boolean} isAmountField - Whether this is an amount field
 * @param {boolean} showPound - Whether to show pound symbol
 * @returns {string|number} - Formatted value with pound symbol or original value
 */
export const formatPoundAmount = (val, isAmountField, showPound) => {
    if (isAmountField && showPound) {
        const cleaned = String(val).replace(/[$,]/g, '').trim();
        const num = Number(cleaned);
        if (!isNaN(num)) {
            return `£${num.toLocaleString()}`;
        }
    }
    return val;
};

/**
 * Formats a numeric amount value
 * @param {string|number} val - The value to format
 * @param {boolean} isAmountField - Whether this is an amount field
 * @returns {string|number} - Formatted value with locale string or original value
 */
export const formatAmount = (val, isAmountField) => {
    if (isAmountField) {
        const cleaned = String(val).replace(/[$,]/g, '').trim();
        const num = Number(cleaned);
        if (!isNaN(num)) {
            return num.toLocaleString();
        }
    }
    return val;
};

/**
 * Formats a dollar amount value
 * @param {string|number} val - The value to format
 * @param {boolean} isAmountField - Whether this is an amount field
 * @param {boolean} showDoller - Whether to show dollar symbol
 * @returns {string|number} - Formatted value with dollar symbol or original value
 */
export const formatDollerAmount = (val, isAmountField, showDoller) => {
    if (isAmountField && showDoller) {
        const cleaned = String(val).replace(/[$,]/g, '').trim();
        const num = Number(cleaned);
        if (!isNaN(num)) {
            return `$${num.toLocaleString()}`;
        }
    }
    return val;
};

/**
 * Formats a percentage amount value
 * @param {string|number} val - The value to format
 * @param {boolean} isAmountField - Whether this is an amount field
 * @param {boolean} showPercentage - Whether to show percentage symbol
 * @returns {string|number} - Formatted value with percentage symbol or original value
 */
export const formatPercentageAmount = (val, isAmountField, showPercentage) => {
    if (isAmountField && showPercentage) {
        const cleaned = String(val).replace(/[%,]/g, '').trim();
        const num = Number(cleaned);
        if (!isNaN(num)) {
            // If the number is less than 1 (and not 0), it's a decimal (e.g., 0.022), convert to percentage (2.2%)
            if (num < 1 && num !== 0) {
                const percentage = num * 100;
                // Format to show up to 1 decimal place, removing trailing zeros
                return `${percentage.toFixed(1).replace(/\.?0+$/, '')}%`;
            }
            // If the number is already >= 1 or is 0, assume it's already a percentage value
            return `${num.toLocaleString()}%`;
        }
    }
    return val;
};

/**
 * Gets the header value based on formData and formatting options
 * @param {Object} formData - The form data object
 * @param {string} value - The key to get the value from
 * @param {Object} obj - Configuration object with formatting options
 * @returns {string|number} - Formatted value or '-' if not found
 */
export const headerValue = (formData, value, obj) => {
    if (formData?.[value]) {
        if(obj?.isAmountField && obj?.isPound) {
            return formatPoundAmount(typeof formData?.[value] === 'object' ? formData?.[value]?.value : formData?.[value], obj?.isAmountField, obj?.isPound);
        }
        if(obj?.isAmountField && obj?.isDoller) {
            return formatDollerAmount(typeof formData?.[value] === 'object' ? formData?.[value]?.value : formData?.[value], obj?.isAmountField, obj?.isDoller);
        }
        if(obj?.isAmountField && obj?.isPercentage) {
            return formatPercentageAmount(typeof formData?.[value] === 'object' ? formData?.[value]?.value : formData?.[value], obj?.isAmountField, obj?.isPercentage);
        }
        if(obj?.isAmountField) {
            return formatAmount(typeof formData?.[value] === 'object' ? formData?.[value]?.value : formData?.[value], obj?.isAmountField);
        }
        if(obj?.isDate) {
            const dataValue = typeof formData?.[value] === 'object' ? formData?.[value]?.value : formData?.[value];
            return toDDMMYY(dataValue);
        }
        return typeof formData?.[value] === 'object' ? formData?.[value]?.value : formData?.[value];
    }
    return '-';
};

export const toDDMMYY = (input) => {
    if (!input) return "";

    const d = input instanceof Date ? input : new Date(input);
    if (Number.isNaN(d.getTime())) return ""; // invalid date

    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = String(d.getFullYear()).slice(-2);

    return `${dd}/${mm}/${yy}`;
};

/**
 * Formats a date to a locale-independent DD-MM-YYYY string.
 * Parses plain "YYYY-MM-DD" strings (the value a native <input type="date"> always
 * produces) directly, to avoid a UTC/local timezone shift moving the day by one.
 * @param {string|Date} input - The date value to format
 * @returns {string} - Formatted date string in DD-MM-YYYY, or "" if invalid/empty
 */
export const toDDMMYYYY = (input) => {
    if (!input) return "";

    if (typeof input === "string" && /^\d{4}-\d{2}-\d{2}/.test(input)) {
        const [yyyy, mm, dd] = input.split("T")[0].split("-");
        return `${dd}-${mm}-${yyyy}`;
    }

    const d = input instanceof Date ? input : new Date(input);
    if (Number.isNaN(d.getTime())) return "";

    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();

    return `${dd}-${mm}-${yyyy}`;
};

/**
 * Formats a timestamp to a readable date string
 * @param {string|Date} timestamp - The timestamp to format
 * @returns {string} - Formatted date string with slashes replaced by dashes
 */
export const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const formatted = date.toLocaleString("en-US", {
        month: "numeric",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    });
    return formatted.replace(/\//g, "-");
};
